import { AbstractControl, ValidationErrors } from '@angular/forms';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { mapControlErrors } from 'src/app/shared/services/validation-helpers';
import { resolveUnitPrecision, UnitPrecisionMap, UnitPrecisionOverrides } from '../../helpers/unit-precision.helper';
import { UnitsGridColumn, UnitsGridNumericColumn } from './units-grid-input.models';

export function isUnitsGridNumericColumn(column: UnitsGridColumn): column is UnitsGridNumericColumn {
  return column.kind === 'number';
}

export function isUnitsGridEditable(column: UnitsGridColumn): boolean {
  return column.editable !== false;
}

export function resolveUnitsGridUnitSymbol(
  column: UnitsGridNumericColumn,
  userUnits: UserUnitsModel | null
): string {
  if (column.unitSymbol) {
    return column.unitSymbol;
  }

  if (!column.unitKey || !userUnits) {
    return '';
  }

  const selectedUnit = userUnits[column.unitKey] as keyof typeof unitsLib | undefined;
  if (!selectedUnit) {
    return '';
  }

  const libraryEntry = unitsLib[selectedUnit];
  return libraryEntry?.symbol ?? selectedUnit;
}

export function buildUnitsGridHeader(
  column: UnitsGridColumn,
  userUnits: UserUnitsModel | null
): string {
  if (!isUnitsGridNumericColumn(column)) {
    return column.header;
  }

  const unitSymbol = resolveUnitsGridUnitSymbol(column, userUnits);
  return unitSymbol ? `${column.header} (${unitSymbol})` : column.header;
}

export function getUnitsGridFractionDigits(
  column: UnitsGridNumericColumn,
  resolvedDecimals: number,
  fractionField: 'minFractionDigits' | 'maxFractionDigits' | 'formatFractionDigits'
): number {
  const override = column[fractionField];
  return override !== undefined ? override : resolvedDecimals;
}

export function getUnitsGridDisplayFractionDigits(
  column: UnitsGridNumericColumn,
  resolvedDecimals: number
): number {
  return column.formatFractionDigits !== undefined ? column.formatFractionDigits : resolvedDecimals;
}

export function formatUnitsGridCellDisplayValue(
  value: unknown,
  column: UnitsGridColumn,
  displayFractionDigits: number
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (isUnitsGridNumericColumn(column)) {
    return Number(value).toLocaleString('en-US', { maximumFractionDigits: displayFractionDigits });
  }

  return String(value);
}

export function getUnitsGridValidationMessage(controlErrors: ValidationErrors | null | undefined): string {
  if (!controlErrors) {
    return '';
  }

  return mapControlErrors(controlErrors);
}

export function resolveUnitsGridPrecision(
  column: UnitsGridNumericColumn,
  userUnits: UserUnitsModel | null,
  unitPrecisionMap: UnitPrecisionMap,
  unitPrecisionOverrides: UnitPrecisionOverrides
): number {
  const unitSymbol = resolveUnitsGridUnitSymbol(column, userUnits);
  return resolveUnitPrecision(unitSymbol, unitPrecisionMap, unitPrecisionOverrides);
}

export function getUnitsGridControlValue(control: AbstractControl | null): unknown {
  return control?.value;
}
